import { lazy, MouseEvent, Suspense, useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Popover from '@mui/material/Popover'
import { ROUTES } from '../../constants/routes'
import { usePermissions } from '../../helpers/permissionContext'
import { useIsMobile } from '../../hooks/useIsMobile'
import Button from '../Common/Button'
import Icon from '../Common/Icon'
import Text from '../Common/Text'
import styles from './NotificationPane.module.scss'
import { PropsFromRedux } from './index'

export type IProps = PropsFromRedux

const NotificationPaneList = lazy(() => import('./NotificationPaneList'))

const NotificationPane = ({ notifications, markAsRead, requestOpenMessagesChannel }: IProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null)
  const { isBDO, isUW, isClientUser, isInternalUser } = usePermissions()
  // Subscribed once here and passed down: `useMediaQuery` registers a listener per
  // calling component, and these rows are a list (see useIsMobile's own note).
  const isMobile = useIsMobile()
  const unreadNotificationIds = useMemo(
    () => notifications?.data.filter(({ isRead }) => !isRead).map(({ id }) => id) || [],
    [notifications],
  )

  const markNotificationAsRead = useCallback(async () => {
    if (unreadNotificationIds.length) {
      await markAsRead({
        notificationIds: unreadNotificationIds,
      })
    }
  }, [unreadNotificationIds, markAsRead])

  const handleOpen = useCallback((event: MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const navigate = useNavigate()

  const handleClose = useCallback(async () => {
    await markNotificationAsRead()
    setAnchorEl(null)
  }, [markNotificationAsRead])

  if (!notifications) {
    return null
  }
  return (
    <>
      <div className={styles.wrapper} onClick={handleOpen}>
        <Icon name="bell" size="md" />
        {notifications.totalUnread > 0 && (
          <span className={styles.countBadge}>{notifications.totalUnread}</span>
        )}
      </div>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 52,
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        classes={{
          paper: styles.popover,
        }}
      >
        {notifications.total ? (
          <>
            <div className={styles.header}>
              <Text variant="display" component="span">
                Notifications {notifications.totalUnread > 0 && `(${notifications.totalUnread})`}
              </Text>
              <div className={styles.headerActions}>
                <Button
                  size="small"
                  onClick={() => {
                    void navigate(ROUTES.NOTIFICATIONS)
                    void handleClose()
                  }}
                >
                  See all
                </Button>
              </div>
            </div>

            <Suspense fallback={null}>
              <NotificationPaneList
                notifications={notifications.data}
                handleClose={handleClose}
                openMessageInBubble={requestOpenMessagesChannel}
                isInternalUser={isInternalUser}
                isMobile={isMobile}
                isBDO={isBDO}
                isUW={isUW}
                isClientUser={isClientUser}
              />
            </Suspense>
          </>
        ) : (
          <div className={styles.notificationsEmpty}>
            {`You don't have any notification at the moment`}
          </div>
        )}
      </Popover>
    </>
  )
}

export default NotificationPane
